import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Download, FileText, FileSpreadsheet, FileType2,
  Recycle, TrendingUp, Leaf, Scale, ChevronDown,
  GitCompareArrows, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const TYPE_COLORS = {
  general: "#4A4D4A", recyclable: "#1A362D", organic: "#557C55",
  hazardous: "#C85A3C", ewaste: "#2C6E49",
};
const METHOD_COLORS = {
  recycling: "#1A362D", composting: "#557C55", reuse: "#2C6E49",
  landfill: "#4A4D4A", incineration: "#C85A3C",
};

const iso = (d) => d.toISOString().slice(0, 10);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const endOfQuarter = (d) => { const s = startOfQuarter(d); return new Date(s.getFullYear(), s.getMonth() + 3, 0); };

const PRESETS = [
  { key: "this_month", label: "This month", range: () => { const n = new Date(); return { start: iso(startOfMonth(n)), end: iso(n) }; } },
  { key: "last_month", label: "Last month", range: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth() - 1, 1); return { start: iso(s), end: iso(endOfMonth(s)) }; } },
  { key: "this_qtr",   label: "This quarter", range: () => { const n = new Date(); return { start: iso(startOfQuarter(n)), end: iso(n) }; } },
  { key: "last_qtr",   label: "Last quarter", range: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth() - 3, 1); return { start: iso(startOfQuarter(s)), end: iso(endOfQuarter(s)) }; } },
  { key: "last_3m",    label: "Last 3 months", range: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth() - 2, 1); return { start: iso(s), end: iso(n) }; } },
  { key: "last_6m",    label: "Last 6 months", range: () => { const n = new Date(); const s = new Date(n.getFullYear(), n.getMonth() - 5, 1); return { start: iso(s), end: iso(n) }; } },
  { key: "ytd",        label: "Year to date", range: () => { const n = new Date(); return { start: `${n.getFullYear()}-01-01`, end: iso(n) }; } },
  { key: "all",        label: "All time", range: () => ({ start: "", end: "" }) },
  { key: "custom",     label: "Custom", range: null },
];

const formatDelta = (a, b, unit = "") => {
  if (b == null || b === 0) return { text: "—", tone: "neutral" };
  const diff = a - b;
  const pct = (diff / b) * 100;
  const sign = diff > 0 ? "+" : diff < 0 ? "" : "";
  return { text: `${sign}${diff.toFixed(unit === "%" ? 1 : 0)}${unit}  (${sign}${pct.toFixed(1)}%)`, tone: diff > 0 ? "positive" : diff < 0 ? "negative" : "neutral" };
};

const downloadFile = async (path, filename) => {
  const token = localStorage.getItem("hbt_token");
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

const KpiCard = ({ icon: Icon, label, value, unit, hint, hintTone = "neutral", compareValue, compareRaw, currentRaw }) => {
  const showCompare = compareValue !== undefined && compareValue !== null;
  const delta = showCompare && currentRaw != null && compareRaw != null ? formatDelta(currentRaw, compareRaw, unit === "%" ? "%" : "") : null;
  return (
    <div className="hbt-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="hbt-label">{label}</div>
        <Icon className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <div className="font-display text-3xl font-semibold font-mono-num">{value}</div>
        {unit && <div className="text-xs text-neutral-500">{unit}</div>}
      </div>
      {showCompare && (
        <div className="mt-3 pt-3 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }} data-testid={`kpi-compare-${label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-400">vs. Period B</div>
            <div className="text-sm font-mono-num text-neutral-600">{compareValue}{unit && ` ${unit}`}</div>
          </div>
          {delta && (
            <div className={`text-[11px] font-medium inline-flex items-center gap-1 ${delta.tone === "positive" ? "text-[var(--forest)]" : delta.tone === "negative" ? "text-[var(--terracotta,#C85A3C)]" : "text-neutral-500"}`}>
              {delta.tone === "positive" ? <ArrowUpRight className="w-3 h-3" /> : delta.tone === "negative" ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
              {delta.text}
            </div>
          )}
        </div>
      )}
      {!showCompare && hint && (
        <div className={`text-[11px] mt-2 ${hintTone === "positive" ? "text-[var(--forest)]" : hintTone === "negative" ? "text-[var(--terracotta,#C85A3C)]" : "text-neutral-500"}`}>
          {hint}
        </div>
      )}
    </div>
  );
};

export default function Reports() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [preset, setPreset] = useState("last_3m");
  const [start, setStart] = useState(PRESETS.find((p) => p.key === "last_3m").range().start);
  const [end, setEnd] = useState(PRESETS.find((p) => p.key === "last_3m").range().end);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [page, setPage] = useState(0);
  const [collections, setCollections] = useState([]);
  const [filter, setFilter] = useState("");
  // ---- Compare mode ----
  const [compareMode, setCompareMode] = useState(false);
  const [presetB, setPresetB] = useState("last_qtr");
  const [startB, setStartB] = useState(PRESETS.find((p) => p.key === "last_qtr").range().start);
  const [endB, setEndB] = useState(PRESETS.find((p) => p.key === "last_qtr").range().end);
  const [summaryB, setSummaryB] = useState(null);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/clients").then((r) => {
        setClients(r.data);
        if (r.data.length && !clientId) setClientId(r.data[0].id);
      });
    } else {
      setClientId(user?.client_id || "");
    }
  }, [user]);

  const activeClient = useMemo(
    () => clients.find((c) => c.id === clientId) || (user?.role === "client" ? { id: user.client_id, name: user.name } : null),
    [clients, clientId, user],
  );

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS.find((x) => x.key === key);
    if (p && p.range) {
      const { start: s, end: e } = p.range();
      setStart(s); setEnd(e);
    }
  };

  const applyPresetB = (key) => {
    setPresetB(key);
    const p = PRESETS.find((x) => x.key === key);
    if (p && p.range) {
      const { start: s, end: e } = p.range();
      setStartB(s); setEndB(e);
    }
  };

  const params = () => {
    const p = new URLSearchParams();
    if (clientId) p.append("client_id", clientId);
    if (start) p.append("start", start);
    if (end) p.append("end", end);
    return p.toString();
  };

  const load = async () => {
    if (user?.role === "admin" && !clientId) return;
    setLoading(true);
    try {
      const q = params();
      const [sum, coll] = await Promise.all([
        api.get(`/analytics/summary?${q}`),
        api.get(`/collections${clientId ? `?client_id=${clientId}` : ""}`),
      ]);
      setSummary(sum.data);
      // client-side date filter for the table (backend filter for summary already done)
      const filtered = coll.data.filter((c) => {
        if (start && (c.collection_date || "") < start) return false;
        if (end && (c.collection_date || "") > end) return false;
        return true;
      });
      setCollections(filtered);
      setPage(0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load report data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId, start, end]);

  // Load Period B when compare mode is on and its range changes
  useEffect(() => {
    if (!compareMode || !clientId) { setSummaryB(null); return; }
    const p = new URLSearchParams();
    p.append("client_id", clientId);
    if (startB) p.append("start", startB);
    if (endB) p.append("end", endB);
    api.get(`/analytics/summary?${p.toString()}`)
      .then((r) => setSummaryB(r.data))
      .catch(() => setSummaryB(null));
  }, [compareMode, clientId, startB, endB]);

  const doExport = async (kind) => {
    if (!clientId) { toast.error("Choose a client"); return; }
    setExporting(kind); setShowExport(false);
    const clientSlug = (activeClient?.name || "client").replace(/\s+/g, "_");
    const slug = start || end ? `${start || "start"}_to_${end || "today"}` : "all";
    try {
      if (kind === "pdf") {
        if (!singleMonth) { toast.error("PDF report covers one calendar month. Pick a single-month range first."); return; }
        await downloadFile(`/reports/monthly?client_id=${clientId}&month=${singleMonth}`, `HBT_Report_${clientSlug}_${singleMonth}.pdf`);
      } else if (kind === "xlsx") {
        await downloadFile(`/reports/period.xlsx?${params()}`, `HBT_Report_${clientSlug}_${slug}.xlsx`);
      } else if (kind === "csv") {
        await downloadFile(`/reports/collections.csv?${params()}`, `HBT_Collections_${clientSlug}_${slug}.csv`);
      } else if (kind === "certificate") {
        if (!singleMonth) { toast.error("Compliance certificate is issued per month. Pick a single-month range first."); return; }
        await downloadFile(`/reports/certificate?client_id=${clientId}&month=${singleMonth}`, `HBT_Certificate_${clientSlug}_${singleMonth}.pdf`);
      }
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally { setExporting(null); }
  };

  const bench = summary?.benchmark;
  const tableRows = useMemo(() => {
    const q = filter.toLowerCase();
    return collections
      .filter((r) =>
        !q ||
        (r.waste_type || "").toLowerCase().includes(q) ||
        (r.disposal_method || "").toLowerCase().includes(q) ||
        (r.notes || "").toLowerCase().includes(q),
      )
      .sort((a, b) => (b.collection_date || "").localeCompare(a.collection_date || ""));
  }, [collections, filter]);
  const rowsPerPage = 20;
  const pageRows = tableRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  const pages = Math.max(1, Math.ceil(tableRows.length / rowsPerPage));

  const periodLabel = PRESETS.find((p) => p.key === preset)?.label || "Custom";

  // Single-month range check — the PDF and Compliance certificate endpoints are month-scoped,
  // so we only enable them when Period A resolves to exactly one calendar month.
  const singleMonth = useMemo(() => {
    if (!start || !end) return null;
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e)) return null;
    if (s.getFullYear() !== e.getFullYear() || s.getMonth() !== e.getMonth()) return null;
    return start.slice(0, 7);
  }, [start, end]);
  const monthLocked = !!singleMonth;

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Sustainability reports"
        subtitle="Explore your waste, diversion and ESG performance across any period — export when ready."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCompareMode((v) => !v)}
              className={`inline-flex items-center gap-2 ${compareMode ? "btn-primary" : "btn-outline"}`}
              data-testid="compare-toggle-btn"
              title="Pin a second period side-by-side"
            >
              <GitCompareArrows className="w-4 h-4" /> {compareMode ? "Comparing" : "Compare"}
            </button>
            <div className="relative">
              <button
                onClick={() => setShowExport((v) => !v)}
                disabled={loading || exporting}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="export-toggle-btn"
              >
                <Download className="w-4 h-4" /> {exporting ? `Exporting…` : "Export"} <ChevronDown className="w-3 h-3" />
              </button>
              {showExport && (
                <div className="absolute right-0 top-full mt-2 hbt-card p-2 z-30 w-64" data-testid="export-menu">
                  <button onClick={() => doExport("pdf")} disabled={!monthLocked} className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-black/[0.04] rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="export-pdf" title={monthLocked ? "Download a monthly PDF report" : "Pick a single-month range (e.g. This month, Last month)"}>
                    <FileText className="w-4 h-4" /> PDF report <span className="ml-auto text-[10px] uppercase tracking-wider text-neutral-400">monthly</span>
                  </button>
                  <button onClick={() => doExport("xlsx")} className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-black/[0.04] rounded text-sm" data-testid="export-xlsx">
                    <FileSpreadsheet className="w-4 h-4" /> Excel workbook <span className="ml-auto text-[10px] uppercase tracking-wider text-neutral-400">any range</span>
                  </button>
                  <button onClick={() => doExport("csv")} className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-black/[0.04] rounded text-sm" data-testid="export-csv">
                    <FileType2 className="w-4 h-4" /> CSV of collections <span className="ml-auto text-[10px] uppercase tracking-wider text-neutral-400">any range</span>
                  </button>
                  <div className="border-t my-2" style={{ borderColor: "var(--border)" }} />
                  <button onClick={() => doExport("certificate")} disabled={!monthLocked} className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-black/[0.04] rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="export-cert" title={monthLocked ? "Download the compliance certificate for the selected month" : "Compliance certificate is issued monthly — pick a single-month range"}>
                    <FileText className="w-4 h-4" /> Compliance certificate <span className="ml-auto text-[10px] uppercase tracking-wider text-neutral-400">monthly</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      />

      <div className="px-8 lg:px-12 py-8 space-y-6" onClick={() => showExport && setShowExport(false)}>
        {/* Period filter */}
        <div className="hbt-card p-5" data-testid="period-filter">
          {compareMode && <div className="hbt-label mb-3" style={{ color: "var(--forest)" }}>Period A (current)</div>}
          <div className="flex flex-wrap items-center gap-4">
            {user?.role === "admin" && (
              <div className="flex items-center gap-2">
                <label className="hbt-label">Client</label>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="hbt-input max-w-[240px]" data-testid="client-select">
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="flex items-center gap-1.5 flex-wrap">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${preset === p.key ? "font-semibold" : "text-neutral-600 hover:text-black"}`}
                  style={{ borderColor: preset === p.key ? "var(--forest)" : "var(--border)", background: preset === p.key ? "rgba(26,54,45,0.08)" : "transparent", color: preset === p.key ? "var(--forest)" : undefined }}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <input type="date" value={start || ""} onChange={(e) => { setStart(e.target.value); setPreset("custom"); }} className="hbt-input text-xs max-w-[150px]" data-testid="range-start" />
              <span className="text-xs text-neutral-400">→</span>
              <input type="date" value={end || ""} onChange={(e) => { setEnd(e.target.value); setPreset("custom"); }} className="hbt-input text-xs max-w-[150px]" data-testid="range-end" />
            </div>
          </div>

          {compareMode && (
            <div className="mt-5 pt-5 border-t" style={{ borderColor: "var(--border)" }} data-testid="period-b-controls">
              <div className="hbt-label mb-3" style={{ color: "var(--terracotta,#C85A3C)" }}>Period B (compare against)</div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {PRESETS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => applyPresetB(p.key)}
                      className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${presetB === p.key ? "font-semibold" : "text-neutral-600 hover:text-black"}`}
                      style={{ borderColor: presetB === p.key ? "var(--terracotta,#C85A3C)" : "var(--border)", background: presetB === p.key ? "rgba(200,90,60,0.10)" : "transparent", color: presetB === p.key ? "var(--terracotta,#C85A3C)" : undefined }}
                      data-testid={`preset-b-${p.key}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <input type="date" value={startB || ""} onChange={(e) => { setStartB(e.target.value); setPresetB("custom"); }} className="hbt-input text-xs max-w-[150px]" data-testid="range-b-start" />
                  <span className="text-xs text-neutral-400">→</span>
                  <input type="date" value={endB || ""} onChange={(e) => { setEndB(e.target.value); setPresetB("custom"); }} className="hbt-input text-xs max-w-[150px]" data-testid="range-b-end" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard icon={Scale}      label="Total waste"    value={(summary?.total_kg || 0).toLocaleString()} unit="kg"
            hint={`${summary?.collection_count || 0} collections logged`}
            currentRaw={summary?.total_kg}
            compareRaw={summaryB?.total_kg}
            compareValue={summaryB ? summaryB.total_kg.toLocaleString() : undefined}
          />
          <KpiCard icon={Recycle}    label="Diversion rate" value={(summary?.diversion_rate || 0).toFixed(1)} unit="%"
            hint={bench ? `Industry avg ${bench.diversion_avg}% · top ${bench.diversion_top}%` : null}
            hintTone={bench ? (summary?.diversion_rate >= bench.diversion_avg ? "positive" : "negative") : "neutral"}
            currentRaw={summary?.diversion_rate}
            compareRaw={summaryB?.diversion_rate}
            compareValue={summaryB ? summaryB.diversion_rate.toFixed(1) : undefined}
          />
          <KpiCard icon={TrendingUp} label="Recycling rate" value={(summary?.recycling_rate || 0).toFixed(1)} unit="%"
            hint={bench ? `Industry avg ${bench.recycling_avg}%` : null}
            hintTone={bench ? (summary?.recycling_rate >= bench.recycling_avg ? "positive" : "negative") : "neutral"}
            currentRaw={summary?.recycling_rate}
            compareRaw={summaryB?.recycling_rate}
            compareValue={summaryB ? summaryB.recycling_rate.toFixed(1) : undefined}
          />
          <KpiCard icon={Leaf}       label="CO2e saved"     value={(summary?.co2_saved_kg || 0).toLocaleString()} unit="kg"
            hint={`ESG score ${summary?.esg_score || 0}/100`}
            currentRaw={summary?.co2_saved_kg}
            compareRaw={summaryB?.co2_saved_kg}
            compareValue={summaryB ? summaryB.co2_saved_kg.toLocaleString() : undefined}
          />
        </div>

        {/* Side-by-side comparison summary */}
        {compareMode && summaryB && (
          <div className="hbt-card p-5" data-testid="compare-summary-card">
            <div className="hbt-label mb-3 flex items-center gap-2"><GitCompareArrows className="w-3 h-3" /> Period comparison</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-md" style={{ background: "rgba(26,54,45,0.06)" }} data-testid="compare-panel-a">
                <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--forest)" }}>Period A</div>
                <div className="text-xs text-neutral-500 mt-1">{start || "beginning"} → {end || "today"}</div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{(summary?.total_kg || 0).toLocaleString()} <span className="text-xs font-normal text-neutral-500">kg</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Diversion</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{(summary?.diversion_rate || 0).toFixed(1)}<span className="text-xs font-normal text-neutral-500">%</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Recycling</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{(summary?.recycling_rate || 0).toFixed(1)}<span className="text-xs font-normal text-neutral-500">%</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Collections</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{summary?.collection_count || 0}</div>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-md" style={{ background: "rgba(200,90,60,0.06)" }} data-testid="compare-panel-b">
                <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--terracotta,#C85A3C)" }}>Period B</div>
                <div className="text-xs text-neutral-500 mt-1">{startB || "beginning"} → {endB || "today"}</div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Total</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{summaryB.total_kg.toLocaleString()} <span className="text-xs font-normal text-neutral-500">kg</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Diversion</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{summaryB.diversion_rate.toFixed(1)}<span className="text-xs font-normal text-neutral-500">%</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Recycling</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{summaryB.recycling_rate.toFixed(1)}<span className="text-xs font-normal text-neutral-500">%</span></div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Collections</div>
                    <div className="font-display text-xl font-semibold font-mono-num">{summaryB.collection_count || 0}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="hbt-card p-5 lg:col-span-8" data-testid="trend-chart-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="hbt-label">Monthly trend</div>
                <div className="font-display text-lg font-semibold">Weight by disposal outcome</div>
              </div>
              <div className="text-xs text-neutral-500">{periodLabel}</div>
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={summary?.trend || []} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(10,10,10,0.08)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid rgba(10,10,10,0.15)", fontSize: 12 }} />
                  <Bar dataKey="diverted" name="Diverted" fill="#1A362D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="landfill" name="Landfill" fill="#C85A3C" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="hbt-card p-5 lg:col-span-4" data-testid="mix-chart-card">
            <div className="hbt-label">Waste mix</div>
            <div className="font-display text-lg font-semibold mb-4">By type</div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={summary?.by_type || []} dataKey="kg" nameKey="type" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {(summary?.by_type || []).map((entry, i) => (
                      <Cell key={i} fill={TYPE_COLORS[entry.type] || "#4A4D4A"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid rgba(10,10,10,0.15)", fontSize: 12 }} formatter={(v) => `${Number(v).toFixed(1)} kg`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 space-y-1.5">
              {(summary?.by_type || []).map((row) => (
                <div key={row.type} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_COLORS[row.type] || "#4A4D4A" }} />
                    <span className="capitalize">{row.type}</span>
                  </div>
                  <span className="font-mono-num text-neutral-600">{row.kg.toFixed(1)} kg</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Method breakdown */}
        <div className="hbt-card p-5" data-testid="method-breakdown">
          <div className="hbt-label">Disposal methods</div>
          <div className="font-display text-lg font-semibold mb-4">Where the waste went</div>
          <div className="space-y-3">
            {(summary?.by_method || []).map((row) => {
              const pct = summary?.total_kg ? (row.kg / summary.total_kg) * 100 : 0;
              return (
                <div key={row.method} data-testid={`method-row-${row.method}`}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <div className="capitalize font-medium">{row.method}</div>
                    <div className="font-mono-num text-neutral-500">{row.kg.toFixed(1)} kg · {pct.toFixed(1)}%</div>
                  </div>
                  <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: "rgba(10,10,10,0.06)" }}>
                    <div className="h-full transition-all" style={{ width: `${pct}%`, background: METHOD_COLORS[row.method] || "#4A4D4A" }} />
                  </div>
                </div>
              );
            })}
            {(!summary?.by_method || summary.by_method.length === 0) && (
              <div className="text-center text-neutral-400 py-6 text-sm">No collections in this period.</div>
            )}
          </div>
        </div>

        {/* Collections table */}
        <div className="hbt-card overflow-hidden" data-testid="collections-table-card">
          <div className="p-5 flex items-center justify-between">
            <div>
              <div className="hbt-label">Log</div>
              <div className="font-display text-lg font-semibold">Collections in this period</div>
            </div>
            <input
              placeholder="Filter by type, method, notes…"
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setPage(0); }}
              className="hbt-input text-xs max-w-[260px]"
              data-testid="collections-filter"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="hbt-table">
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Method</th><th className="num">Weight</th><th>Status</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <tr key={r.id} data-testid={`collection-row-${r.id}`}>
                    <td className="font-mono-num text-xs">{r.collection_date}</td>
                    <td><span className="pill forest capitalize">{r.waste_type}</span></td>
                    <td className="capitalize text-xs">{r.disposal_method}</td>
                    <td className="num font-mono-num">{r.weight_kg?.toFixed?.(1) ?? r.weight_kg} kg</td>
                    <td><span className="pill capitalize">{r.status || "logged"}</span></td>
                    <td className="text-xs text-neutral-600 max-w-[220px] truncate">{r.notes || "—"}</td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-neutral-400 py-14">No collections match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="p-4 flex items-center justify-between text-xs border-t" style={{ borderColor: "var(--border)" }}>
              <div className="text-neutral-500">Page {page + 1} of {pages} · {tableRows.length} rows</div>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-outline text-xs">Prev</button>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="btn-outline text-xs">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
